import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
} from "react";

import { getPricingPeriodValue } from "features/subscriptions/utils";

import type {
  AgencySubscription,
  Pricing,
  PricingPeriodInfo,
  RentedSubscription,
  Subscription,
  SubscriptionType,
} from "common/types";

interface ManageSubscriptionContextType {
  initialState:
    | {
        subscription: AgencySubscription | Subscription | RentedSubscription;
        seats: number;
      }
    | SubscriptionType;
  seatQuantity: number;
  setSeatQuantity: Dispatch<SetStateAction<number>>;
  pricing: { plan: Pricing; addon: Pricing };
  setPricingPeriodInfo: Dispatch<SetStateAction<PricingPeriodInfo>>;
}

const ManageSubscriptionContext =
  createContext<ManageSubscriptionContextType>(null);

interface ManageSubscriptionContextProviderProps {
  children: ReactNode;
  contextValue: ManageSubscriptionContextType;
}

export function ManageSubscriptionContextProvider({
  children,
  contextValue,
}: ManageSubscriptionContextProviderProps) {
  return (
    <ManageSubscriptionContext.Provider value={contextValue}>
      {children}
    </ManageSubscriptionContext.Provider>
  );
}

type useManageSubscriptionReturn = Omit<
  ManageSubscriptionContextType,
  "initialState"
> & {
  subscriptionType: SubscriptionType;
  hasActiveSubscription: boolean;
  isDowngrade: boolean;
  isUnchanged: boolean;
  isPricingPeriodDowngrade: boolean;
  isCancellation: boolean;
  //set when managing an existing (non-cancelled) rental order, which has
  //different downgrade rules than the other subscription types
  rentalOrderSubscription: RentedSubscription | undefined;
} & (
    | {
        hasExistingSubscription: true;
        initialState: {
          subscription: AgencySubscription | Subscription | RentedSubscription;
          seats: number;
        };
      }
    | { hasExistingSubscription: false; initialState: SubscriptionType }
  );

export function useManageSubscription() {
  const context = useContext(ManageSubscriptionContext);
  const { pricing, initialState, seatQuantity } = context;

  const hasExistingSubscription = typeof initialState !== "string";
  const subscriptionType = hasExistingSubscription
    ? initialState.subscription.type
    : initialState;

  let hasActiveSubscription = false;
  let isDowngrade = false;
  let isUnchanged = false;
  let isPricingPeriodDowngrade = false;
  let rentalOrderSubscription: RentedSubscription | undefined;

  if (hasExistingSubscription) {
    const { subscription } = initialState;

    hasActiveSubscription = subscription.status !== "cancelled";
    isDowngrade = seatQuantity < initialState.seats;

    if (subscription.type === "rental" && hasActiveSubscription) {
      rentalOrderSubscription = subscription;
    }

    if (pricing?.plan) {
      isPricingPeriodDowngrade =
        getPricingPeriodValue(pricing.plan) <
        getPricingPeriodValue(subscription.item_price);

      isUnchanged =
        seatQuantity === initialState.seats &&
        pricing.plan.id === subscription.item_price.id;
    }
  }

  return {
    subscriptionType,
    hasExistingSubscription,
    hasActiveSubscription,
    isDowngrade,
    isUnchanged,
    isPricingPeriodDowngrade,
    isCancellation: seatQuantity === 0,
    rentalOrderSubscription,
    ...context,
  } as useManageSubscriptionReturn;
}
